/**
 * NewAgentWizard — v2.0 rewrite (§9 of DEVELOPMENT_PLAN.md).
 *
 * Four steps, rendered as a full-page form. Owns its own state (no
 * global store pollution — the entire wizard lives and dies in this
 * component). Deploy is inline: no modal, no persistent drawer. On
 * success we navigate to the agent detail page and toss a toast.
 *
 *   1. Identity      — name, description, permissions mode, restart
 *   2. Discord Token — paste + "Test connection" gate
 *   3. Channels      — DiscordChannelPicker + user ID + DM policy
 *   4. Review        — card summary + large "Deploy" button
 *
 * Users can skip Discord (step 2 → step 4) to create a channel-less
 * agent. Step 3 is automatically skipped in that case.
 *
 * Types are declared locally so we don't have to touch the shared
 * `@/api/agents` module which still ships the v1 field names.
 */
import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  ExternalLink,
  HelpCircle,
  Loader2,
  Rocket,
  Server,
  X as XIcon,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/api/client';
import {
  validateDiscordToken,
  type DiscordBotInfo,
} from '@/api/channels';
import { DiscordChannelPicker } from '@/components/DiscordChannelPicker';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ---------- local types (do not leak to @/api/agents) ----------

type V2PermissionsMode =
  | 'bypassPermissions'
  | 'acceptEdits'
  | 'plan'
  | 'default';

type V2DmPolicy = 'allowlist' | 'pairing' | 'disabled';

type EffortLevel = 'low' | 'medium' | 'high' | 'max';

interface V2AgentCreatePayload {
  name: string;
  description: string;
  permissions_mode: V2PermissionsMode;
  effort: EffortLevel;
  disable_adaptive_thinking: boolean;
  restart_sec: number;
  discord?: {
    enabled: boolean;
    bot_token: string;
    user_ids: string[];
    channel_ids: string[];
    dm_policy: V2DmPolicy;
  } | null;
}

/** AgentOut — the full agent returned by POST /api/agents on success. */
interface V2AgentOut {
  name: string;
  description: string;
  permissions_mode: V2PermissionsMode;
  restart_sec: number;
  enabled?: boolean;
  status?: string;
}

interface WizardState {
  // Step 1
  name: string;
  description: string;
  permissionsMode: V2PermissionsMode;
  effort: EffortLevel;
  disableAdaptiveThinking: boolean;
  restartSec: number;

  // Step 2
  useDiscord: boolean;
  botToken: string;
  botInfo: DiscordBotInfo | null;
  tokenValidated: boolean;

  // Step 3
  channelIds: string[];
  userIds: string[];
  dmPolicy: V2DmPolicy;
}

const STEPS = [
  { key: 'identity', label: 'Identity' },
  { key: 'token', label: 'Discord Token' },
  { key: 'channels', label: 'Channels' },
  { key: 'review', label: 'Review' },
] as const;

type StepKey = (typeof STEPS)[number]['key'];

const SLUG_REGEX = /^[a-z][a-z0-9-]{0,31}$/;

const initialState: WizardState = {
  name: '',
  description: '',
  permissionsMode: 'bypassPermissions',
  effort: 'medium',
  disableAdaptiveThinking: false,
  restartSec: 15,
  useDiscord: true,
  botToken: '',
  botInfo: null,
  tokenValidated: false,
  channelIds: [],
  userIds: [],
  dmPolicy: 'allowlist',
};

// ---------- root component ----------

export default function NewAgentWizard() {
  const navigate = useNavigate();
  const [state, setState] = useState<WizardState>(initialState);
  const [stepIdx, setStepIdx] = useState(0);
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);

  const step = STEPS[stepIdx].key;

  const identityValid = SLUG_REGEX.test(state.name) && state.description.trim().length > 0;
  const tokenValid = state.useDiscord ? state.tokenValidated : true;
  const channelsValid = state.useDiscord
    ? state.channelIds.length > 0 || state.userIds.length > 0
    : true;

  /**
   * Determines if the user can advance from `from`. The special case is
   * the token step when the user has clicked "I'll configure Discord
   * later" — we jump straight to review.
   */
  function canAdvance(from: StepKey): boolean {
    switch (from) {
      case 'identity':
        return identityValid;
      case 'token':
        return tokenValid;
      case 'channels':
        return channelsValid;
      case 'review':
        return true;
    }
  }

  function goNext() {
    if (!canAdvance(step)) return;
    // If Discord is skipped, jump from step 2 (token) straight to step 4 (review).
    if (step === 'token' && !state.useDiscord) {
      setStepIdx(STEPS.findIndex((s) => s.key === 'review'));
      return;
    }
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  }

  function goBack() {
    // If Discord is skipped, jump from step 4 (review) back to step 2 (token).
    if (step === 'review' && !state.useDiscord) {
      setStepIdx(STEPS.findIndex((s) => s.key === 'token'));
      return;
    }
    setStepIdx((i) => Math.max(i - 1, 0));
  }

  function handleCancel() {
    navigate('/agents');
  }

  async function handleDeploy() {
    setDeployError(null);
    setDeploying(true);
    try {
      const payload: V2AgentCreatePayload = {
        name: state.name,
        description: state.description,
        permissions_mode: state.permissionsMode,
        effort: state.effort,
        disable_adaptive_thinking: state.disableAdaptiveThinking,
        restart_sec: state.restartSec,
        discord: state.useDiscord
          ? {
              enabled: true,
              bot_token: state.botToken,
              user_ids: state.userIds,
              channel_ids: state.channelIds,
              dm_policy: state.dmPolicy,
            }
          : null,
      };
      const created = await api<V2AgentOut>('/agents', {
        method: 'POST',
        body: payload,
      });
      toast.success(`Agent "${created.name ?? state.name}" is live.`);
      navigate(`/agents/${encodeURIComponent(created.name ?? state.name)}`);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail ?? err.title
          : (err as Error)?.message ?? 'Failed to deploy agent.';
      setDeployError(msg);
    } finally {
      setDeploying(false);
    }
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="mx-auto w-full max-w-3xl space-y-6 p-4 md:p-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Create a new agent
            </h1>
            <p className="text-sm text-muted-foreground">
              Four short steps. You can edit everything later from the agent's
              detail page.
            </p>
          </div>
          <Button variant="outline" onClick={handleCancel} type="button">
            Cancel
          </Button>
        </header>

        <Stepper step={stepIdx} />

        <Card>
          <CardContent className="pt-6">
            {step === 'identity' && (
              <IdentityStep state={state} setState={setState} />
            )}
            {step === 'token' && (
              <TokenStep
                state={state}
                setState={setState}
                onSkip={() => {
                  setState((s) => ({
                    ...s,
                    useDiscord: false,
                    tokenValidated: true,
                  }));
                  setStepIdx(STEPS.findIndex((s) => s.key === 'review'));
                }}
              />
            )}
            {step === 'channels' && (
              <ChannelsStep state={state} setState={setState} />
            )}
            {step === 'review' && <ReviewStep state={state} />}
          </CardContent>
        </Card>

        {deployError && (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            role="alert"
            aria-live="polite"
          >
            {deployError}
          </div>
        )}

        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={goBack}
            disabled={stepIdx === 0 || deploying}
            type="button"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back
          </Button>
          {step === 'review' ? (
            <Button
              onClick={handleDeploy}
              disabled={deploying || !identityValid || !tokenValid || !channelsValid}
              size="lg"
              type="button"
            >
              {deploying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Deploying…
                </>
              ) : (
                <>
                  <Rocket className="h-4 w-4" aria-hidden />
                  Deploy
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={goNext}
              disabled={!canAdvance(step)}
              type="button"
            >
              Next
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

// ---------- stepper ----------

function Stepper({ step }: { step: number }) {
  return (
    <ol
      className="flex items-center gap-1 text-xs"
      aria-label="Wizard steps"
    >
      {STEPS.map((s, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <li key={s.key} className="flex flex-1 items-center gap-2">
            <div
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold',
                active && 'border-primary text-primary',
                done && 'border-primary bg-primary text-primary-foreground',
                !active && !done && 'border-border text-muted-foreground'
              )}
              aria-current={active ? 'step' : undefined}
            >
              {done ? <Check className="h-3 w-3" aria-hidden /> : i + 1}
            </div>
            <span
              className={cn(
                'hidden sm:inline',
                active ? 'font-semibold text-foreground' : 'text-muted-foreground'
              )}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <div
                className="mx-1 flex-1 border-t border-dashed border-border"
                aria-hidden
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ---------- step 1 — identity ----------

function IdentityStep({
  state,
  setState,
}: {
  state: WizardState;
  setState: Dispatch<SetStateAction<WizardState>>;
}) {
  const nameInvalid = state.name !== '' && !SLUG_REGEX.test(state.name);
  const urlPreview = state.name ? `/agents/${state.name}` : '/agents/<name>';

  return (
    <section aria-labelledby="step-identity-heading" className="space-y-4">
      <h2 id="step-identity-heading" className="sr-only">
        Step 1 — Identity
      </h2>

      <div className="space-y-2">
        <Label htmlFor="agent-name">Name</Label>
        <Input
          id="agent-name"
          autoFocus
          value={state.name}
          onChange={(e) =>
            setState((s) => ({ ...s, name: e.target.value.toLowerCase() }))
          }
          placeholder="e.g. news"
          aria-invalid={nameInvalid}
          aria-describedby="agent-name-help"
        />
        <p id="agent-name-help" className="text-xs text-muted-foreground">
          Lowercase letters, digits, and dashes. Must start with a letter. Max 32 chars.
        </p>
        <p className="text-xs text-muted-foreground">
          Slug preview: <code className="font-mono text-foreground">{urlPreview}</code>
        </p>
        {nameInvalid && (
          <p className="text-xs text-destructive" aria-live="polite">
            Name must match <code className="font-mono">[a-z][a-z0-9-]{'{0,31}'}</code>.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="agent-description">Description</Label>
        <textarea
          id="agent-description"
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          rows={3}
          value={state.description}
          onChange={(e) =>
            setState((s) => ({ ...s, description: e.target.value }))
          }
          placeholder="What does this agent do?"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="permissions-mode">Permissions mode</Label>
          <select
            id="permissions-mode"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            value={state.permissionsMode}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                permissionsMode: e.target.value as V2PermissionsMode,
              }))
            }
          >
            <option value="bypassPermissions">Bypass permissions (recommended)</option>
            <option value="acceptEdits">Accept edits automatically</option>
            <option value="plan">Plan mode (read-only)</option>
            <option value="default">Default (ask each time)</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="restart-sec">Restart delay (seconds)</Label>
          <Input
            id="restart-sec"
            type="number"
            min={1}
            max={600}
            value={state.restartSec}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                restartSec: Number(e.target.value) || 0,
              }))
            }
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="effort-level">Effort level</Label>
        <select
          id="effort-level"
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          value={state.effort}
          onChange={(e) =>
            setState((s) => ({ ...s, effort: e.target.value as EffortLevel }))
          }
        >
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="max">Max</option>
        </select>
        <p className="text-xs text-muted-foreground">
          How hard the model thinks. Higher = slower but more thorough.
        </p>
      </div>

      <div className="flex items-center justify-between rounded-md border border-border p-4">
        <div>
          <Label htmlFor="disable-adaptive-thinking" className="text-base">
            Disable adaptive thinking
          </Label>
          <p className="text-sm text-muted-foreground">
            Force Claude to always reason, even on simple questions. Useful
            at high/max effort.
          </p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2">
          <span className="sr-only">Disable adaptive thinking</span>
          <input
            id="disable-adaptive-thinking"
            type="checkbox"
            className="h-4 w-4 accent-primary"
            checked={state.disableAdaptiveThinking}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                disableAdaptiveThinking: e.target.checked,
              }))
            }
          />
        </label>
      </div>
    </section>
  );
}

// ---------- step 2 — Discord token ----------

function TokenStep({
  state,
  setState,
  onSkip,
}: {
  state: WizardState;
  setState: Dispatch<SetStateAction<WizardState>>;
  onSkip: () => void;
}) {
  const [showToken, setShowToken] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  async function handleTest() {
    if (!state.botToken) return;
    setTesting(true);
    setTestError(null);
    try {
      const info = await validateDiscordToken(state.botToken);
      setState((s) => ({
        ...s,
        botInfo: info,
        tokenValidated: true,
        useDiscord: true,
      }));
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail ?? err.title
          : (err as Error)?.message ?? 'Failed to contact Discord';
      setTestError(msg);
      setState((s) => ({ ...s, tokenValidated: false, botInfo: null }));
    } finally {
      setTesting(false);
    }
  }

  return (
    <section aria-labelledby="step-token-heading" className="space-y-4">
      <h2 id="step-token-heading" className="sr-only">
        Step 2 — Discord Token
      </h2>

      <p className="text-sm text-muted-foreground">
        A Discord bot token lets this agent connect to the Discord Gateway and
        respond to messages in channels it has been added to. You create one in
        the{' '}
        <a
          className="text-primary underline underline-offset-2 hover:text-primary/80"
          href="https://discord.com/developers/applications"
          target="_blank"
          rel="noreferrer"
        >
          Discord Developer Portal
          <ExternalLink className="ml-1 inline h-3 w-3" aria-hidden />
        </a>
        . Keep it secret — we encrypt it at rest.
      </p>

      <div className="space-y-2">
        <Label htmlFor="bot-token">Bot token</Label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              id="bot-token"
              type={showToken ? 'text' : 'password'}
              value={state.botToken}
              onChange={(e) => {
                const value = e.target.value;
                setState((s) => ({
                  ...s,
                  botToken: value,
                  tokenValidated: false,
                  botInfo: null,
                }));
                setTestError(null);
              }}
              placeholder="MTIz…xyz"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setShowToken((v) => !v)}
              aria-label={showToken ? 'Hide token' : 'Show token'}
            >
              {showToken ? (
                <EyeOff className="h-4 w-4" aria-hidden />
              ) : (
                <Eye className="h-4 w-4" aria-hidden />
              )}
            </button>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleTest}
            disabled={!state.botToken || testing}
          >
            {testing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Testing…
              </>
            ) : (
              'Test connection'
            )}
          </Button>
        </div>
        <div aria-live="polite">
          {state.tokenValidated && state.botInfo && (
            <p className="flex items-center gap-1 text-sm text-success">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              Connected as{' '}
              <strong>
                {state.botInfo.username}
                {state.botInfo.discriminator && state.botInfo.discriminator !== '0'
                  ? `#${state.botInfo.discriminator}`
                  : ''}
              </strong>
              {typeof state.botInfo.guild_count === 'number' && (
                <span className="text-muted-foreground">
                  · sees {state.botInfo.guild_count} guild
                  {state.botInfo.guild_count === 1 ? '' : 's'}
                </span>
              )}
            </p>
          )}
          {testError && (
            <p
              className="flex items-center gap-1 text-sm text-destructive"
              role="alert"
            >
              <XCircle className="h-4 w-4" aria-hidden />
              {testError}
            </p>
          )}
        </div>
      </div>

      <div className="pt-2 text-xs">
        <button
          type="button"
          onClick={onSkip}
          className="text-primary underline underline-offset-2 hover:text-primary/80"
        >
          I'll configure Discord later
        </button>
      </div>
    </section>
  );
}

// ---------- step 3 — channels ----------

function ChannelsStep({
  state,
  setState,
}: {
  state: WizardState;
  setState: Dispatch<SetStateAction<WizardState>>;
}) {
  const [newUserId, setNewUserId] = useState('');

  function addUserId() {
    const trimmed = newUserId.trim();
    if (!trimmed || !/^\d{15,25}$/.test(trimmed)) return;
    if (!state.userIds.includes(trimmed)) {
      setState((s) => ({ ...s, userIds: [...s.userIds, trimmed] }));
    }
    setNewUserId('');
  }

  function removeUserId(id: string) {
    setState((s) => ({
      ...s,
      userIds: s.userIds.filter((u) => u !== id),
    }));
  }

  return (
    <section aria-labelledby="step-channels-heading" className="space-y-5">
      <h2 id="step-channels-heading" className="sr-only">
        Step 3 — Channels
      </h2>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Channels</Label>
          <span className="text-xs text-muted-foreground">
            {state.channelIds.length} channel
            {state.channelIds.length === 1 ? '' : 's'} selected
          </span>
        </div>
        <DiscordChannelPicker
          token={state.botToken}
          selectedChannelIds={state.channelIds}
          onChange={(ids) => setState((s) => ({ ...s, channelIds: ids }))}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-1">
          <Label htmlFor="user-id-input">Your Discord user ID</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                aria-label="How do I find my Discord user ID?"
              >
                <HelpCircle className="h-3.5 w-3.5" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="mb-1 font-semibold">Finding your user ID</p>
              <ol className="list-decimal pl-4 text-[11px] leading-relaxed">
                <li>Open Discord → Settings → Advanced</li>
                <li>Toggle Developer Mode on</li>
                <li>Right-click your name → "Copy User ID"</li>
              </ol>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {state.userIds.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary/60 px-2 py-0.5 text-xs font-mono"
            >
              {id}
              <button
                type="button"
                onClick={() => removeUserId(id)}
                aria-label={`Remove ${id}`}
                className="rounded hover:text-destructive"
              >
                <XIcon className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
          {state.userIds.length === 0 && (
            <span className="text-xs italic text-muted-foreground">
              No users allowlisted yet.
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Input
            id="user-id-input"
            value={newUserId}
            onChange={(e) => setNewUserId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addUserId();
              }
            }}
            placeholder="e.g. 123456789012345678"
            inputMode="numeric"
          />
          <Button
            type="button"
            variant="outline"
            onClick={addUserId}
            disabled={!newUserId.trim()}
          >
            Add
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="dm-policy">DM policy</Label>
        <select
          id="dm-policy"
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          value={state.dmPolicy}
          onChange={(e) =>
            setState((s) => ({ ...s, dmPolicy: e.target.value as V2DmPolicy }))
          }
        >
          <option value="allowlist">Allowlist only — only listed user IDs can DM</option>
          <option value="pairing">Pairing — channel pair ↔ DM</option>
          <option value="disabled">Disabled — ignore all DMs</option>
        </select>
      </div>
    </section>
  );
}

// ---------- step 4 — review ----------

function ReviewStep({ state }: { state: WizardState }) {
  const permissionsLabel = useMemo(() => {
    switch (state.permissionsMode) {
      case 'bypassPermissions':
        return 'Bypass permissions';
      case 'acceptEdits':
        return 'Accept edits automatically';
      case 'plan':
        return 'Plan (read-only)';
      case 'default':
        return 'Default (ask each time)';
    }
  }, [state.permissionsMode]);

  return (
    <section aria-labelledby="step-review-heading" className="space-y-4">
      <h2 id="step-review-heading" className="sr-only">
        Step 4 — Review
      </h2>
      <p className="text-sm text-muted-foreground">
        Confirm the details below. Clicking Deploy will create the agent and
        start its systemd service.
      </p>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Bot className="h-4 w-4 text-primary" aria-hidden />
            <span className="font-mono">{state.name || '<unnamed>'}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-xs">
          <ReviewRow label="Description">
            {state.description || <span className="text-muted-foreground">—</span>}
          </ReviewRow>
          <ReviewRow label="Permissions mode">{permissionsLabel}</ReviewRow>
          <ReviewRow label="Effort level">
            {state.effort.charAt(0).toUpperCase() + state.effort.slice(1)}
          </ReviewRow>
          <ReviewRow label="Adaptive thinking">
            {state.disableAdaptiveThinking ? 'Disabled (always reason)' : 'Enabled (default)'}
          </ReviewRow>
          <ReviewRow label="Restart delay">{state.restartSec}s</ReviewRow>
          <div className="my-2 border-t border-dashed border-border" aria-hidden />
          {state.useDiscord ? (
            <>
              <ReviewRow label="Discord bot">
                {state.botInfo ? (
                  <span className="font-semibold">
                    {state.botInfo.username}
                    {state.botInfo.discriminator && state.botInfo.discriminator !== '0'
                      ? `#${state.botInfo.discriminator}`
                      : ''}
                  </span>
                ) : (
                  <span className="text-muted-foreground">validated</span>
                )}
              </ReviewRow>
              <ReviewRow label="Channels">
                {state.channelIds.length} selected
              </ReviewRow>
              <ReviewRow label="User IDs">
                {state.userIds.length > 0
                  ? state.userIds.join(', ')
                  : <span className="text-muted-foreground">none</span>}
              </ReviewRow>
              <ReviewRow label="DM policy">{state.dmPolicy}</ReviewRow>
            </>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Server className="h-3.5 w-3.5" aria-hidden />
              Discord will be configured later.
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Something off?{' '}
        <Link
          to="/agents/new"
          onClick={(e) => {
            // Link kept for a11y — the parent's Back button actually walks us
            // backwards through the wizard state.
            e.preventDefault();
          }}
          className="text-primary underline underline-offset-2"
        >
          Click Back to edit.
        </Link>
      </p>
    </section>
  );
}

function ReviewRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="w-32 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="flex-1 text-right">{children}</dd>
    </div>
  );
}
